import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from "@mui/material/styles";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { SubmitHandler, SubmitErrorHandler, useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, AlertTitle, Typography } from "@mui/material";
import { useAuth } from "../../_auth/useAuth";
import {
    ButtonBar, DaButton,
    DaAutocompleteField, DaCheckboxField, DaHiddenField, DaTextField,
    DaNumberFormatField, DaDateTimePickerField,
    DaTabPanel, HTMLComment,
    Form, FormSectionHeader, FormTitle,
    IMessagePanelProps, MessagePanel, ProgressPanel,
    MessageBox, MsgBoxType,
    MuiGridDataProp, MuiGridStateProp,
    NumberFormatType,
    DaGridContainer, DaGridLoadingOverlay, DaGridSettings,
    ChildGridToolbar,
    IDialogTextProps, DialogBox,
    DaSignatureField,
    ShowMore,
} from "../_shared";
import { AevTab, AevTabs, FormPaper } from "./Paf.styles";
import {
    IItemIdRequest, IItemIdsRequest, IPaf, pafApi,
    ILookupNAICS, ILookupSearchTerm, ILookupState, lookupApi,
    ILicensee,
    licenseeApi,
    customerApi,
    ICustomer,
    IUser
} from "../../_api";
import { AuthRolesEnum, pafAEVFields, pafFilesColumns } from "../../_appData";
import { DataGrid, GridLinkOperator, GridRowId } from "@mui/x-data-grid";
import { IFileUploadDialogTextProps } from "../_shared/file_upload/types";
import { FileUploadDialog } from "../_shared/file_upload/FileUploadDialog";
import { throttle } from 'lodash';
import { BrokerSelection } from './BrokerSelection';
import { CompanySelection } from './CompanySelection';
import { EsignSelection } from './EsignSelection';
import { SignatureStatusIndicator } from './SignatureStatusIndicator';
import { createPafSchema } from './paf.schema';
import { TAB_FIELDS } from './constant';
import { useBrokerAutoFill } from './useBrokerAutoFill';

export const PafAEView: React.FC = () => {


    const theme = useTheme();
    const { user, userHasPrivilege, getAccessToken } = useAuth();
    const [searchParams] = useSearchParams();
    const isViewMode = searchParams.get("mode") === "view";
    const location = useLocation();
    const navigationState = location.state as { newBrokerSid?: string; navigateToTab?: number } | null;

    // State declarations
    const navigate = useNavigate();
    const params = useParams();
    const [filesSelectionModel, setFilesSelectionModel] = useState<GridRowId[]>([]);
    const [msgPanelProps, setMsgPanelProps] = useState<IMessagePanelProps>({ message: "" });
    const [progressTitle, setProgressTitle] = useState<string>("Getting Licensee Info...");
    const [showError, setShowError] = useState<boolean>(false);
    const [showProgress, setShowProgress] = useState<boolean>(true);
    const [dlgDelFilesOpen, setDlgDelFilesOpen] = React.useState<boolean>(false);
    const [dlgDelFilesProps, setDlgDelFilesProps] = useState<IDialogTextProps>({ message: "" });
    const [dlgFilesUploadOpen, setDlgFilesUploadOpen] = React.useState<boolean>(false);
    const [dlgFilesUploadProps, setDlgFilesUploadProps] = useState<IFileUploadDialogTextProps>({ title: "Upload File" });
    const [dlgConfirmOpen, setDlgConfirmOpen] = React.useState<boolean>(false);
    const [dlgConfirmProps, setDlgConfirmProps] = useState<IDialogTextProps>({ message: "" });
    const [dlgRejectOpen, setDlgRejectOpen] = React.useState<boolean>(false);
    const [rejectionReason, setRejectionReason] = useState<string>('');
    const [msgBoxOpen, setMsgBoxOpen] = React.useState<boolean>(false);
    const [msgBoxProps, setMsgBoxProps] = useState<IMessagePanelProps>({ message: "" });
    const [selTabIndex, setSelTabIndex] = useState<number>(0);
    const [isFilesLoading, setIsFilesLoading] = useState<boolean>(true);
    const [signatureStatus, setSignatureStatus] = useState<string>('');
    const [stateOptions, setStateOptions] = useState<Array<ILookupState>>([]);
    const [naicsOptions, setNaicsOptions] = useState<Array<ILookupNAICS>>([]);
    const [authorizedUsers, setAuthorizedUsers] = useState<Array<IUser>>([]);

    // Computed values based on state
    const isApproved = signatureStatus === 'APPROVED';
    const isRejected = signatureStatus === 'REJECTED';
    const isWaitingApproval = signatureStatus === 'WAITING_APPROVAL';
    const isPendingWithBroker = signatureStatus === 'WAITING_SIGNATURES';
    const isExpired = signatureStatus === 'EXPIRED';
    const isNoneOrRejected = signatureStatus === 'NONE' || signatureStatus === 'REJECTED' || signatureStatus === '';
    const isLicenseeUser = userHasPrivilege([AuthRolesEnum.Licensee, AuthRolesEnum.SystemAdmin]);
    const canEditLicensee = isViewMode && !isApproved && userHasPrivilege([
        AuthRolesEnum.Licensee,
        AuthRolesEnum.SystemAdmin,
    ]);
    const canApproveReject = canEditLicensee && isWaitingApproval;
    const canEditListOwner = isViewMode && isNoneOrRejected && userHasPrivilege([AuthRolesEnum.ListOwner]);
    const canSignOnly = isViewMode && !isApproved && userHasPrivilege([
        AuthRolesEnum.BrokerAgent,
        AuthRolesEnum.ListAdmin,
    ]);
    const canSaveInView = isViewMode && (canEditLicensee || canSignOnly || canEditListOwner);
    // Everything except Licensee tab and Signatures tab is read-only in view mode, or everything if approved
    // ListOwner can edit when status is NONE or REJECTED
    // const isReadOnlyNonSign = (isViewMode && !canEditListOwner) || isApproved;
    const isLicenseeRequired = userHasPrivilege([
        AuthRolesEnum.Licensee,
        AuthRolesEnum.SystemAdmin,
    ])
    const pafSchema = useMemo(
        () =>
            createPafSchema({
                isLicenseeRequired,
            }),
        [isLicenseeRequired]
    )

    const [filesGridState, setFilesGridState] = useState<MuiGridStateProp>({
        page: 0,
        page_size: DaGridSettings.defaultPageSize,
        filters: {
            items: [],
            linkOperator: GridLinkOperator.Or
        },
        sorts: [],
    });
    const [pafFilesData, setPafFilesData] = useState<MuiGridDataProp>({
        rows: [],
        row_count: 0,
    });

    const formFieldSize = "medium";
    const formFieldVariant = "standard";
    let pafId: string | undefined = undefined;

    let aevMode: "add" | "upd" | "err" | undefined = undefined;


    // Validate URL param
    if (params.id) {
        if (params.id.length !== 36) {
            if (params.id !== "new") {
                aevMode = "err";
            } else {
                pafId = "new";
                aevMode = "add";
            }
        } else {
            pafId = params.id;
            aevMode = "upd";
        }
    }

    // Edit mode check - in edit mode, only Notes, Documents, and Broker selection are editable
    const isEditMode = !isViewMode && aevMode === "upd";
    const isAddMode = aevMode === "add";

    // Tab-specific readonly variables - MUST be after isReadOnlyNonSign is defined
    // View Mode: ALL tabs are ALWAYS disabled for everyone (no editing allowed)
    // Edit Mode: Company, Address, Contact, Broker fields are disabled. Only Notes, Documents, and broker selection are editable.
    // EXPIRED status: Treat like view mode - all fields disabled, view only
    // If paf_sid (which contains company_sid value) is present: Company and Address tabs are disabled (for Licensee users as well)
    // Note: hasPafSid will be computed after useForm hook
    let isCompanyReadOnly = isViewMode || isEditMode || isExpired || !userHasPrivilege([AuthRolesEnum.Licensee]);
    let isAddressReadOnly = isViewMode || isEditMode || isExpired || !userHasPrivilege([AuthRolesEnum.Licensee]);
    // Contact fields: editable in add mode, disabled in view mode, edit mode, or EXPIRED status
    const isContactReadOnly = isViewMode || isEditMode || isExpired;
    const isNotesReadOnly = isViewMode || isApproved || isExpired; // View mode or EXPIRED: always disabled. Edit/add mode: only readonly if approved
    const isBrokerTabReadOnly = isViewMode || isEditMode || isExpired; // View mode or EXPIRED: always disabled. Edit mode: fields readonly but broker selection enabled separately
    // Licensee users can upload/delete documents in view mode only when status is WAITING_APPROVAL
    // For other users: view mode disabled, edit/add mode only readonly if approved
    // EXPIRED status: documents are readonly
    const isContactReadyOnlyInAddMode = isAddMode;
    const isDocumentsReadOnly = (isViewMode && (!isLicenseeUser || !isWaitingApproval)) || isApproved || isExpired;
    // defining the initial state for the form
    const initialValues = {
        paf_id: '',
        paf_sid: '',
        company__company_name: '',
        company__parent_company_name: '',
        company__dba_name: '',
        company__address1: '',
        company__address2: '',
        company__city: '',
        company__state: '',
        company__postal_code: '',
        company__federal_tax_id: '',
        company__naics_code: '',
        company__phone: '',
        company__representative_name: '',
        company__representative_title: '',
        company__representative_email: '',
        company__company_website: '',
        company__usps_mailer_id: '',
        notes: '',
        is_internal: false,
        is_foreign_location: false,
        is_ncoa: false,
        is_dsf: false,
        is_triplex: false,
        is_waiver_on_file: false,
        is_deleted: false,
        create_datetime: new Date(),
        modify_datetime: new Date(),
        modify_user_name: `${user.account_id}`,
        licensee_id: -1,
        licensee__company_name: '',
        licensee__officer_name: '',
        licensee__officer_title: '',
        licensee__federal_tax_id: '',
        licensee_acct_exec: '',
        licensee_sales_office: '',
        licensee_operator: '',
        listadmin_id: null,
        listadmin__company_name: '',
        listadmin__address1: '',
        listadmin__address2: '',
        listadmin__city: '',
        listadmin__state: '',
        listadmin__postal_code: '',
        listadmin__phone: '',
        listadmin__naics_code: '',
        licensee_signature_attest: false,
        licensee_signature_text: '',
        licensee_signature_date: null,
        licensee_signature_rejection_reason: '',
        list_admin_signature_attest: false,
        listadmin_signature_text: '',
        listadmin_signature_date: null,
        list_owner_signature_attest: false,
        listowner_signature_text: '',
        listowner_signature_date: null,
        representative_selection: null,
    };


    const { control, getValues, handleSubmit, setValue, reset, resetField, watch, trigger, formState: { errors } } = useForm<IPaf>({
        defaultValues: initialValues,
        resolver: yupResolver(pafSchema, { abortEarly: false }),
        mode: "onChange",
        reValidateMode: "onChange",
    });

    // Watch paf_sid (which contains company_sid value) to disable Company and Address tabs when it's present
    const pafSid = watch("paf_sid");
    const hasPafSid = !!pafSid && pafSid !== '';

    // Update readonly flags to include hasPafSid condition
    isCompanyReadOnly = isViewMode || isEditMode || isExpired || hasPafSid || !userHasPrivilege([AuthRolesEnum.Licensee]);
    isAddressReadOnly = isViewMode || isEditMode || isExpired || hasPafSid || !userHasPrivilege([AuthRolesEnum.Licensee]);

    // Helper: check if logged-in user role is in pending_signatures (must be after useForm)
    const pendingSignatures = (watch('pending_signatures' as any) || []) as string[];
    const isListOwnerPending = pendingSignatures.includes('list_owner');
    const isBrokerPending = pendingSignatures.includes('broker');

    // Hook for broker auto-fill functionality
    const { autoFillBrokerBySid } = useBrokerAutoFill(setValue);

    // Watch representative email to control List Owner e-signature visibility
    const representativeEmail = watch('company__representative_email' as any);
    const isRepresentativeEmailMatch = representativeEmail && user.email &&
        representativeEmail.toLowerCase().trim() === user.email.toLowerCase().trim();
    const brokerEmail = watch('listadmin__representative_email' as any);
    const isBrokerEmailMatch = brokerEmail && user.email &&
        brokerEmail.toLowerCase().trim() === user.email.toLowerCase().trim();

    // Helper functions to check if signatures are present (reactive)
    const listOwnerSignatureText = watch('list_owner_signature_text' as any);
    const listOwnerSignatureDate = watch('list_owner_signature_date' as any);
    const isListOwnerSignaturePresent: boolean = useMemo(() => {
        return !!(listOwnerSignatureText && listOwnerSignatureDate);
    }, [listOwnerSignatureText, listOwnerSignatureDate]);

    const listAdminSignatureText = watch('list_admin_signature_text' as any);
    const listAdminSignatureDate = watch('list_admin_signature_date' as any);
    const listAdminSid = watch('listadmin__listadmin_sid' as any);
    const hasBrokerSid = useMemo(() => {
        return !!(listAdminSid && listAdminSid.trim() !== '');
    }, [listAdminSid]);
    const isListAdminSignaturePresent: boolean = useMemo(() => {
        return !!(listAdminSignatureText && listAdminSignatureDate);
    }, [listAdminSignatureText, listAdminSignatureDate]);

    const licenseeSignatureText = watch('licensee_signature_text' as any);
    const licenseeSignatureDate = watch('licensee_signature_date' as any);
    const licenseeSignatureRejectionReason = watch('licensee_signature_rejection_reason' as any) as string | undefined;
    const isLicenseeSignaturePresent: boolean = useMemo(() => {
        return !!(licenseeSignatureText && licenseeSignatureDate);
    }, [licenseeSignatureText, licenseeSignatureDate]);

    // Watch representative selection dropdown to update representative fields
    const representativeSelection = watch('representative_selection' as any);
    const showLicenseeSignatureSection = (userHasPrivilege([AuthRolesEnum.Licensee, AuthRolesEnum.SystemAdmin])) || (userHasPrivilege([AuthRolesEnum.ListAdmin, AuthRolesEnum.ListOwner]) && (signatureStatus === 'REJECTED' || signatureStatus === 'APPROVED'))

    const dlgDelFiles_Close = (value?: string) => {
        const selRows = filesSelectionModel;
        setDlgDelFilesOpen(false);

        if (value && value === "ok") {
            // delete
            deleteSelectedItems(selRows as (string | number)[]);
        }
    }

    const dlgDelFiles_Show = (message: string, title?: string) => {
        setDlgDelFilesProps({
            message: message,
            title: title,
        })
        setDlgDelFilesOpen(true);
    }

    const dlgConfirm_Close = (value?: string) => {
        setDlgConfirmOpen(false);
        if (value && value === "ok") {
            navigate("/pafs");
        }
    }

    const dlgConfirm_Show = (type: 'reject' | 'cancel') => {
        if (type === 'reject') {
            setRejectionReason('');
            setDlgRejectOpen(true);
        } else {
            setDlgConfirmProps({
                message: "Are you sure you want to cancel?",
                title: "Cancel Changes",
            })
            setDlgConfirmOpen(true);
        }
    }

    const dlgReject_Close = (value?: string) => {
        if (value && value === "ok" && rejectionReason.trim()) {
            // Submit form with rejection
            const formData = getValues();
            const rejectionData = {
                ...formData,
                licensee_signature_attest: true,
                licensee_signature_text: formData.licensee__officer_name,
                licensee_signature_date: new Date(),
                licensee_signature_rejection_reason: rejectionReason
            };
            savePafData(rejectionData);
        }
        setDlgRejectOpen(false);
        setRejectionReason('');
    }

    const handleCancelClick = () => {
        dlgConfirm_Show('cancel');
    }

    const dlgFileUpload_Show = (title?: string) => {
        setDlgFilesUploadProps({
            title: title,
        })

        setDlgFilesUploadOpen(true);
    }

    const dlgFileUpload_Close = (value?: string, selFiles?: FileList) => {
        if (value && value === "ok") {
            // Upload File
            if (pafId && pafId !== "new" && selFiles && selFiles.length > 0) {
                // uploadFile(selFiles[0]);
                const formData = new FormData()
                formData.append("paf_id", pafId);
                formData.append("content", selFiles[0]);



                addPafFiles(formData);
            }
        }
        setDlgFilesUploadOpen(false);
    }

    const addPafFiles = useCallback(async (options): Promise<void> => {
        setProgressTitle("Uploading selected File...");
        setShowProgress(true);

        const auth_token = await getAccessToken();
        let apiPromise = undefined;
        apiPromise = pafApi.files_add(options, auth_token);

        apiPromise && apiPromise.then((resp) => {
            if (resp && resp.success) {
                getPafFilesData();
            } else {
                showErrorPanel({
                    title: "Unable to Upload File",
                    message: "An error occurred while uploading File. " +
                        "Verify that you have provided all required data and " +
                        "retry your last action."
                });
            }
        })
            .catch(() => {
                showErrorPanel({
                    title: "PAF API Save Error",
                    message: "An error occurred while upload File data. " +
                        "There may have been a network error.",
                });
            })
            .finally(() => {
                setShowProgress(false);
            });
    }, []);

    const msgBox_Close = (value?: string) => {
        setMsgBoxOpen(false);
    }

    const msgBox_Show = (message: string, title?: string, type?: MsgBoxType) => {
        setMsgBoxProps({
            message: message,
            title: title,
            type: type,
        })
        setMsgBoxOpen(true);
    }

    const onApprove = () => {
        const formData = getValues();
        const approveData = {
            ...formData,
            licensee_signature_attest: true,
            licensee_signature_text: formData.licensee__officer_name,
            licensee_signature_date: new Date(),
        };
        savePafData(approveData);
    };

    const onSubmit: SubmitHandler<IPaf> = (data) => {
        savePafData(data);
    };

    const onSubmitErrors: SubmitErrorHandler<IPaf> = (errors) => {
        const messages: string[] = [];

        const walk = (obj: any) => {
            Object.values(obj).forEach((val: any) => {
                if (val?.message) messages.push(val.message);
                if (val && typeof val === "object") walk(val);
            });
        };

        walk(errors);

        msgBox_Show(
            `Please fix the following required fields:\n\n${messages
                .map((m) => `• ${m}`)
                .join("\n")}`,
            "Invalid PAF Info",
            "error"
        );
    };


    const showErrorPanel = ({ title, message, type = "error" }: IMessagePanelProps) => {
        msgBox_Show(
            `${title ? title + "\n\n" : ""}${message}`,
            title,
            "error"
        );
    }

    const handleTabChange = (e: React.SyntheticEvent, idx: number) => {
        setSelTabIndex(idx);
    }

    const handleNext = async () => {
        const fields = TAB_FIELDS[selTabIndex];

        if (!fields || fields.length === 0) {
            setSelTabIndex((prev) => prev + 1);
            return;
        }

        const isTabValid = await trigger(fields, {
            shouldFocus: true,
        });

        if (!isTabValid) return;

        setSelTabIndex((prev) => prev + 1);
    };

    const handlePrevious = () => {
        setSelTabIndex((prev) => prev - 1);
    };


    const onClickFileAdd = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {

        // Display File Upload dialog
        dlgFileUpload_Show("Choose File to Upload");
    }, []);


    const onClickFileDelete = (e: React.MouseEvent<HTMLButtonElement>) => {

        // see dlgDelFiles_Close for processing of dialog result
        dlgDelFiles_Show(
            "Please confirm that you wish to delete the selected item(s).",
            "Delete Selected Item(s)?"
        );
    };

    const onClickFileReset = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        setFilesGridState((prev) => ({
            ...prev,
            filters: {
                items: [],
                linkOperator: GridLinkOperator.Or
            },
            sorts: []
        }));
    }, [filesGridState]);

    const formatDateForApi = (date: any): string | null | undefined => {
        if (!date) return date;
        if (typeof date === 'string') {
            // If it's already a string, check if it needs time component
            if (date.includes('T')) return date;
            // Add time component for date-only strings
            return `${date}T00:00:00.000Z`;
        }
        if (date instanceof Date) {
            return date.toISOString();
        }
        return date;
    };

    const savePafData = useCallback(async (options, redirectPath?: string, navigationState?: any): Promise<void> => {
        setProgressTitle("Saving PAF Info...");
        setShowProgress(true);

        // Automatically set signature_attest to true if there's a valid signature
        if (options.licensee_signature_text && options.licensee_signature_date) {
            options.licensee_signature_attest = true;
        }
        if (options.listowner_signature_text && options.listowner_signature_date) {
            options.list_owner_signature_attest = true;
        }
        if (options.listadmin_signature_text && options.listadmin_signature_date) {
            options.list_admin_signature_attest = true;
        }

        // Format all date fields to ISO datetime format
        const formattedOptions = {
            ...options,
            expiration_date: formatDateForApi(options.expiration_date),
            list_owner_signature_date: formatDateForApi(options.list_owner_signature_date),
            list_admin_signature_date: formatDateForApi(options.list_admin_signature_date),
            company__customer_since_date: formatDateForApi(options.company__customer_since_date),
            company__company_since_date: formatDateForApi(options.company__company_since_date),
            licensee_signature_date: formatDateForApi(options.licensee_signature_date),
            listadmin_signature_date: formatDateForApi(options.listadmin_signature_date),
            listowner_signature_date: formatDateForApi(options.listowner_signature_date),
        };

        const auth_token = await getAccessToken();
        let apiPromise = undefined;
        if (aevMode === "add") {
            apiPromise = pafApi.add(formattedOptions, auth_token);
        } else if (aevMode === "upd") {
            apiPromise = pafApi.update(formattedOptions, auth_token);
        } else {
            showErrorPanel({
                title: "Inconsistent Form State Error",
                message: "An error occurred while saving PAF data. " +
                    "Navigate to the PAF list view and retry your last action."
            });
            setShowProgress(false);
            return;
        }

        apiPromise && apiPromise.then((resp) => {
            if (resp && resp.success) {
                // Check if we have a custom redirect path (for broker creation)
                if (redirectPath) {
                    // For new PAFs, update the navigationState with the actual PAF ID from response
                    if (aevMode === "add" && resp.data?.paf_id && navigationState) {
                        const updatedNavigationState = {
                            ...navigationState,
                            brokerCreationPafId: resp.data.paf_id
                        };
                        navigate(redirectPath, { state: updatedNavigationState });
                    } else {
                        navigate(redirectPath, navigationState ? { state: navigationState } : undefined);
                    }
                } else {
                    navigate("/pafs");
                }
            } else {
                showErrorPanel({
                    title: "Unable to Save PAF",
                    message: "An error occurred while saving PAF data. " +
                        "Verify that you have provided all required data and " +
                        "retry your last action."
                });
            }
        })
            .catch(() => {
                showErrorPanel({
                    title: "PAF API Save Error",
                    message: "An error occurred while saving PAF data. " +
                        "There may have been a network error.",
                });
            })
            .finally(() => {
                setShowProgress(false);
            });
    }, []);

    const saveBeforeNavigate = useCallback(async (navigationPath: string, navigationState?: any): Promise<void> => {
        const formData = getValues();
        await savePafData(formData, navigationPath, navigationState);
    }, [getValues, savePafData]);

    // Lookup Field methods
    const stateSearch = React.useRef(
        throttle(async (search_term) => {
            const term = typeof search_term === 'string' ? search_term : '';
            if (term.length < 2) {
                setStateOptions([]);
            } else {
                setStateOptions(await getStateOptions(term) as Array<ILookupState>);
            }
        }, 250)
    ).current;

    const stateInputChanged = async (e: React.SyntheticEvent, newValue: string, reason: string): Promise<void> => {

        // Don't re-run search for
        // programmatic change, user selection
        if (reason !== "reset") {
            stateSearch(typeof newValue === 'string' ? newValue : '');
        }
    }

    const getStateOptions = async (search_term: string): Promise<Array<ILookupState> | void> => {
        const options: ILookupSearchTerm = { search_term: search_term };
        const auth_token = await getAccessToken();
        try {
            const resp = await lookupApi.get_state_lookup(options, auth_token);
            if (resp) {
                if (resp.success) {
                    return resp.data;
                } else if (resp.error && resp.error.length > 0) {
                    showErrorPanel({
                        title: "State lookups Not Found Error",
                        message: "An error occurred while retrieving State lookups data. " +
                            "The State lookups data was not found.",
                    });
                }
            }
        }
        catch (err) {
            showErrorPanel({
                title: "Lookup API Fetch Error",
                message: "An error occurred while fetching State lookups data. " +
                    "There may have been a network error.",
            });
        }
    };

    const naicsSearch = React.useRef(
        throttle(async (search_term) => {
            const term = typeof search_term === 'string' ? search_term : '';
            if (term.length < 3) {
                setNaicsOptions([]);
            } else {
                setNaicsOptions(await getNaicsOptions(term) as Array<ILookupNAICS>);
            }
        }, 250)
    ).current;

    const naicsInputChanged = async (e: React.SyntheticEvent, newValue: string, reason: string): Promise<void> => {

        // Don't re-run search for
        // programmatic change, user selection
        if (reason !== "reset") {
            naicsSearch(typeof newValue === 'string' ? newValue : '');
        }
    }

    const getNaicsOptions = async (search_term: string): Promise<Array<ILookupNAICS> | void> => {
        const options: ILookupSearchTerm = { search_term: search_term };
        const auth_token = await getAccessToken();
        try {
            const resp = await lookupApi.get_naics_lookup(options, auth_token);
            if (resp) {
                if (resp.success) {
                    return resp.data;
                } else if (resp.error && resp.error.length > 0) {
                    showErrorPanel({
                        title: "NAICS lookups Not Found Error",
                        message: "An error occurred while retrieving NAICS lookups data. " +
                            "The NAICS lookups data was not found.",
                    });
                }
            }
        }
        catch (err) {
            showErrorPanel({
                title: "Lookup API Fetch Error",
                message: "An error occurred while fetching NAICS lookups data. " +
                    "There may have been a network error.",
            });
        }
    };


    const getPafFilesData = useCallback(async (): Promise<void> => {
        setIsFilesLoading(true);

        const options: IItemIdRequest = { id: pafId as string };
        const auth_token = await getAccessToken();

        pafApi.files_get_by_paf_id(options, auth_token)
            .then((resp) => {
                if (resp) {
                    if (resp.success) {
                        if (resp.data) {
                            setPafFilesData({
                                rows: resp.data,
                                row_count: resp.count!,
                            });
                        }
                    } else if (resp.error && resp.error.length > 0) {
                        showErrorPanel({
                            title: "PAF Documents Not Found Error",
                            message: "An error occurred while retrieving PAF Documents data. " +
                                "The PAF Documents data was not found.",
                        });
                    }
                }
            })
            .catch(() => {
                showErrorPanel({
                    title: "PAF API Fetch Error",
                    message: "An error occurred while fetching PAF Documents data. " +
                        "There may have been a network error.",
                });
            })
            .finally(() => {
                setIsFilesLoading(false);
            });
    }, [pafId]);

    const getCustomerDataByEmail = useCallback(async (userId: string | number): Promise<void> => {
        if (!userId) return;

        const auth_token = await getAccessToken();

        try {
            const resp = await pafApi.get_by_user_id(userId, auth_token);

            if (resp && resp.data) {
                const data = resp.data;
                const company = data.company || {};
                const userData = data.user || {};
                const title = data.title || '';

                // Use options to ensure form recognizes the values
                const setValueOptions = { shouldValidate: true, shouldDirty: true };

                // Populate company section fields from company object
                if (company.company_name) {
                    setValue("company__company_name" as any, company.company_name, setValueOptions);
                }
                if (company.parent_company_name) {
                    setValue("company__parent_company_name" as any, company.parent_company_name, setValueOptions);
                }
                if (company.dba_name !== undefined) {
                    setValue("company__dba_name" as any, company.dba_name || '', setValueOptions);
                }
                if (company.is_foreign_location !== undefined) {
                    setValue("is_foreign_location" as any, company.is_foreign_location, setValueOptions);
                }
                if (company.is_ncoa !== undefined) {
                    setValue("is_ncoa" as any, company.is_ncoa, setValueOptions);
                }
                if (company.is_dsf !== undefined) {
                    setValue("is_dsf" as any, company.is_dsf, setValueOptions);
                }
                if (company.is_triplex !== undefined) {
                    setValue("is_triplex" as any, company.is_triplex, setValueOptions);
                }
                if (company.is_waiver_on_file !== undefined) {
                    setValue("is_waiver_on_file" as any, company.is_waiver_on_file, setValueOptions);
                }
                if (company.is_internal !== undefined) {
                    setValue("company__is_internal" as any, company.is_internal, setValueOptions);
                }
                if (company.address1) {
                    setValue("company__address1" as any, company.address1, setValueOptions);
                }
                if (company.address2 !== undefined) {
                    setValue("company__address2" as any, company.address2 || '', setValueOptions);
                }
                if (company.city) {
                    setValue("company__city" as any, company.city, setValueOptions);
                }
                if (company.state) {
                    setValue("company__state" as any, company.state, setValueOptions);
                    // Trigger state search to populate options
                    stateSearch(company.state);
                }
                if (company.postal_code) {
                    setValue("company__postal_code" as any, company.postal_code, setValueOptions);
                }
                if (company.urbanization !== undefined) {
                    setValue("company__urbanization" as any, company.urbanization || '', setValueOptions);
                }
                if (company.federal_tax_id !== undefined) {
                    setValue("company__federal_tax_id" as any, company.federal_tax_id || '', setValueOptions);
                }
                if (company.naics_code) {
                    setValue("company__naics_code" as any, company.naics_code, setValueOptions);
                    // Trigger NAICS search to populate options
                    naicsSearch(company.naics_code);
                }
                if (company.usps_mailer_id !== undefined) {
                    setValue("company__usps_mailer_id" as any, company.usps_mailer_id || '', setValueOptions);
                }
                if (company.company_registration_id !== undefined) {
                    setValue("company__company_registration_id" as any, company.company_registration_id || '', setValueOptions);
                }
                if (company.company_website !== undefined) {
                    setValue("company__company_website" as any, company.company_website || '', setValueOptions);
                }
                if (company.carrier_route_id !== undefined) {
                    setValue("company__carrier_route_id" as any, company.carrier_route_id || '', setValueOptions);
                }
                // Phone can be from company or user object
                if (company.phone) {
                    setValue("company__phone" as any, company.phone, setValueOptions);
                } else if (userData.phone) {
                    setValue("company__phone" as any, userData.phone, setValueOptions);
                }

                // Populate contact section fields from user object
                const userName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim();
                if (userName) {
                    setValue("company__representative_name" as any, userName, setValueOptions);
                }
                if (userData.email) {
                    setValue("company__representative_email" as any, userData.email, setValueOptions);
                }
                // Representative title from company object if available
                if (title) {
                    setValue("company__representative_title" as any, title, setValueOptions);
                }

                // Populate checkbox fields from PAF data if available
                const pafData = data.paf || {};
                if (pafData.is_foreign_location !== undefined) {
                    setValue("is_foreign_location" as any, pafData.is_foreign_location, setValueOptions);
                }
                if (pafData.is_ncoa !== undefined) {
                    setValue("is_ncoa" as any, pafData.is_ncoa, setValueOptions);
                }
                if (pafData.is_dsf !== undefined) {
                    setValue("is_dsf" as any, pafData.is_dsf, setValueOptions);
                }
                if (pafData.is_triplex !== undefined) {
                    setValue("is_triplex" as any, pafData.is_triplex, setValueOptions);
                }
                if (pafData.is_waiver_on_file !== undefined) {
                    setValue("is_waiver_on_file" as any, pafData.is_waiver_on_file, setValueOptions);
                }
                // Also check if checkbox values are at the top level of data (for backward compatibility)
                if (data.is_foreign_location !== undefined) {
                    setValue("is_foreign_location" as any, data.is_foreign_location, setValueOptions);
                }
                if (data.is_ncoa !== undefined) {
                    setValue("is_ncoa" as any, data.is_ncoa, setValueOptions);
                }
                if (data.is_dsf !== undefined) {
                    setValue("is_dsf" as any, data.is_dsf, setValueOptions);
                }
                if (data.is_triplex !== undefined) {
                    setValue("is_triplex" as any, data.is_triplex, setValueOptions);
                }
                if (data.is_waiver_on_file !== undefined) {
                    setValue("is_waiver_on_file" as any, data.is_waiver_on_file, setValueOptions);
                }
            } else if (resp && resp.error && resp.error.length > 0) {
                // Silently fail - user data may not exist yet, which is fine for new PAF
            }
        } catch {
            // Silently fail - user data may not exist yet, which is fine for new PAF
        }
    }, [setValue, getAccessToken, naicsSearch, stateSearch]);

    /**
     * Fetches authorized users from customer API and populates the dropdown
     * This is called when creating a new PAF to show authorized users in contact section
     */
    const fetchAuthorizedUsers = useCallback(async (): Promise<void> => {
        const auth_token = await getAccessToken();

        try {
            const resp = await customerApi.get_customer_company_details(auth_token);
            if (resp && resp.success && resp.data) {
                const customerData: ICustomer = resp.data;
                // Set authorized users if available
                if (customerData.authorized_users && customerData.authorized_users.length > 0) {
                    setAuthorizedUsers(customerData.authorized_users);
                } else {
                    setAuthorizedUsers([]);
                }
            } else {
                setAuthorizedUsers([]);
            }
        } catch {
            // Silently fail - authorized users may not exist, which is fine
            setAuthorizedUsers([]);
        }
    }, [getAccessToken]);

    const getPafData = useCallback(async (): Promise<void> => {
        setProgressTitle("Getting PAF Data...");
        setShowProgress(true);
        if (aevMode === "err") {
            showErrorPanel({
                title: "Invalid PAF ID",
                message: "An error occurred while retrieving PAF data. " +
                    "Please go to the PAF list view and retry your latest action."
            });
            setShowProgress(false);
            setIsFilesLoading(false);
        } else if (aevMode === "add") {
            // New PAF - Auto-fill contact info and company data for ListOwner
            if (userHasPrivilege([AuthRolesEnum.ListOwner])) {
                // Fetch and populate company data from user API
                if (user.id) {
                    await getCustomerDataByEmail(user.id);
                }
                // Fetch authorized users for dropdown in contact section
                await fetchAuthorizedUsers();
            }
            setShowProgress(false);
            setIsFilesLoading(false);
        } else if (aevMode === "upd") {
            const options: IItemIdRequest = { id: pafId as string };
            const auth_token = await getAccessToken();

            pafApi.get_by_id(options, auth_token)
                .then((resp) => {
                    if (resp) {
                        if (resp.success) {
                            if (resp.data.files) {
                                setPafFilesData({
                                    rows: resp.data.files,
                                    row_count: resp.data.files.length,
                                });
                            }
                            const data = { ...resp.data };
                            setSignatureStatus(data.signature_status || '');

                            // Map API response fields to form field names if needed
                            // API returns parent_company_name, dba_name at top level
                            // Form expects company__parent_company_name, company__dba_name
                            if (data.parent_company_name !== undefined && !data.company__parent_company_name) {
                                data.company__parent_company_name = data.parent_company_name;
                            }
                            if (data.dba_name !== undefined && !data.company__dba_name) {
                                data.company__dba_name = data.dba_name;
                            }

                            // Auto-fill licensee signature for Licensee users before reset
                            const isLicenseeRole = userHasPrivilege([AuthRolesEnum.Licensee]);
                            const isSystemAdmin = userHasPrivilege([AuthRolesEnum.SystemAdmin]);
                            const licenseeOfficerNameValue = data.licensee__officer_name;
                            const signatureStatus = data.signature_status === 'APPROVED' || data.signature_status === 'REJECTED';
                            if (isLicenseeRole && !isSystemAdmin && licenseeOfficerNameValue && signatureStatus) {
                                data.licensee_signature_text = licenseeOfficerNameValue;
                                data.licensee_signature_date = new Date();
                                data.licensee_signature_attest = true;
                            }
                            reset(data as IPaf);
                        } else if (resp.error && resp.error.length > 0) {
                            showErrorPanel({
                                title: "PAF Not Found Error",
                                message: "An error occurred while retrieving PAF data. " +
                                    "The PAF data was not found.",
                            });
                        }
                    }
                })
                .catch(() => {
                    showErrorPanel({
                        title: "PAF API Fetch Error",
                        message: "An error occurred while fetching PAF data. " +
                            "There may have been a network error.",
                    });
                })
                .finally(() => {
                    const fieldValues = getValues(["company__naics_code", "company__state"]);
                    const [naicsVal, stateVal] = fieldValues as (string | undefined)[];
                    if (naicsVal && typeof naicsVal === 'string') {
                        naicsSearch(naicsVal);
                    } else {
                        setNaicsOptions([]);
                    }
                    if (stateVal && typeof stateVal === 'string') {
                        stateSearch(stateVal);
                    } else {
                        setStateOptions([]);
                    }

                    // Auto-fill broker if returning from broker creation
                    if (navigationState?.newBrokerSid) {
                        const brokerSid = navigationState.newBrokerSid;

                        // Set tab to Broker immediately when broker SID is present
                        if (navigationState.navigateToTab !== undefined) {
                            setSelTabIndex(navigationState.navigateToTab);
                        }

                        // Then auto-fill broker data
                        autoFillBrokerBySid(brokerSid).then((success) => {
                            if (success) {
                                // Clear navigation state after successful auto-fill
                                window.history.replaceState({}, document.title);
                            }
                        });
                    }

                    setShowProgress(false);
                    setIsFilesLoading(false);
                });
        }

    }, [reset, pafId, aevMode, setValue, userHasPrivilege, user, getAccessToken, naicsSearch, stateSearch, navigationState, getCustomerDataByEmail, fetchAuthorizedUsers]);

    const deleteSelectedItems = useCallback(async (row_ids: Array<string | number>) => {
        setIsFilesLoading(true);

        const options: IItemIdsRequest = { item_ids: row_ids };
        const auth_token: string = await getAccessToken();

        pafApi.files_remove(options, auth_token)
            .then((resp) => {
                if (resp) {
                    if (resp.success) {
                        getPafFilesData();
                        msgBox_Show(
                            `${resp.count} PAF Document(s) have been deleted...`,
                            "Delete PAF Documents",
                            "info"
                        );
                    } else if (resp.error && resp.error.length > 0) {
                        msgBox_Show(
                            "An error occurred while deleting PAF Documents... " +
                            "please retry you last action. If you continue to experience issues, " +
                            "contact customer support.",
                            "Unable to Delete PAF Documents...",
                            "warning"
                        );
                    }
                }
            })
            .finally(() => {
                setIsFilesLoading(false);
            });
    }, []);
    const defaultLicenseeId = 1;
    const getLicenseeData = useCallback(async (): Promise<void> => {
        const auth_token = await getAccessToken();

        let licensee: ILicensee = {
            "licensee_id": -1,
            "company_name": "",
            "officer_name": "",
            "officer_title": "",
            "phone": "",
            "fax": "",
            "federal_tax_id": "",
        }
        licenseeApi.get_licensee_details(auth_token)
            .then((resp) => {
                if (resp) {
                    if (resp.success) {
                        if (resp.data) {
                            licensee = resp.data;
                        }
                    } else if (resp.error && resp.error.length > 0) {
                        showErrorPanel({
                            title: "Licensee Not Found Error",
                            message: "An error occurred while retrieving Licensee data. " +
                                "The Licensee data was not found.",
                        });
                    }
                }
            })
            .catch(() => {
                showErrorPanel({
                    title: "Licensee API Fetch Error",
                    message: "An error occurred while fetching Licensee data. " +
                        "There may have been a network error.",
                });
            })
            .finally(() => {
                // Update Licensee data on PAF record
                setValue("licensee_id", licensee.licensee_id);
                setValue("licensee__company_name", licensee.company_name);
                setValue("licensee__officer_name", licensee.officer_name);
                setValue("licensee__officer_title", licensee.officer_title);
                setValue("licensee__federal_tax_id", licensee.federal_tax_id);

                // Auto-fill licensee signature for Licensee users (not SystemAdmin) in add mode
                // const isLicenseeRole = userHasPrivilege([AuthRolesEnum.Licensee]);
                // const isSystemAdmin = userHasPrivilege([AuthRolesEnum.SystemAdmin]);

                // if (isLicenseeRole && !isSystemAdmin && licensee.officer_name && aevMode === 'upd') {
                //     setValue("licensee_signature_text" as any, licensee.officer_name);
                //     setValue("licensee_signature_date" as any, new Date());
                //     setValue("licensee_signature_attest" as any, true);
                // }
            });

    }, [defaultLicenseeId, pafId])


    useEffect(() => {
        // Get data from remote service
        const resp = getPafData();
        getLicenseeData();
        return () => {
            naicsSearch.cancel();
            stateSearch.cancel();
        };

    }, [getPafData, naicsSearch, stateSearch]);

    // // Auto-fill licensee signature after data is loaded
    // useEffect(() => {
    //     const isLicenseeRole = userHasPrivilege([AuthRolesEnum.Licensee]);
    //     const isSystemAdmin = userHasPrivilege([AuthRolesEnum.SystemAdmin]);

    //     // Auto-fill for Licensee users (not SystemAdmin) when officer name is available
    //     if (isLicenseeRole && !isSystemAdmin && licenseeOfficerName) {
    //         setValue("licensee_signature_text" as any, licenseeOfficerName);
    //         setValue("licensee_signature_date" as any, new Date());
    //         setValue("licensee_signature_attest" as any, true);
    //     }
    // }, [licenseeOfficerName, userHasPrivilege, setValue]);

    // Handle representative selection from dropdown
    useEffect(() => {
        if (representativeSelection && authorizedUsers.length > 0) {
            const selectedUser = authorizedUsers.find(u => u.email === representativeSelection);
            if (selectedUser && selectedUser.id) {
                const setValueOptions = { shouldValidate: true, shouldDirty: true };

                // Call getCustomerDataByEmail to auto-populate all company details for selected user
                // This will populate company fields AND contact fields from the API response
                getCustomerDataByEmail(selectedUser.id).then(() => {
                    // Use setTimeout to ensure form state is updated after API call completes
                    setTimeout(() => {
                        // Ensure contact fields are set from selected user (in case API response doesn't have user data)
                        const userName = `${selectedUser.first_name || ''} ${selectedUser.last_name || ''}`.trim();
                        if (userName) {
                            setValue("company__representative_name" as any, userName, setValueOptions);
                        }
                        if (selectedUser.email) {
                            setValue("company__representative_email" as any, selectedUser.email, setValueOptions);
                        }
                        if (selectedUser.phone) {
                            setValue("company__phone" as any, selectedUser.phone, setValueOptions);
                        }
                        // Clear the selection field after updating
                        setValue("representative_selection" as any, null, setValueOptions);
                    }, 100);
                }).catch(() => {
                    // Even if API fails, set contact fields from selected user
                    const userName = `${selectedUser.first_name || ''} ${selectedUser.last_name || ''}`.trim();
                    if (userName) {
                        setValue("company__representative_name" as any, userName, setValueOptions);
                    }
                    if (selectedUser.email) {
                        setValue("company__representative_email" as any, selectedUser.email, setValueOptions);
                    }
                    if (selectedUser.phone) {
                        setValue("company__phone" as any, selectedUser.phone, setValueOptions);
                    }
                    setValue("representative_selection" as any, null, setValueOptions);
                });
            }
        }
    }, [representativeSelection, authorizedUsers, setValue, getCustomerDataByEmail]);

    const filesGridToolbar = () => {
        return (
            <ChildGridToolbar
                showEditButton={false}
                onClickAdd={onClickFileAdd}
                onClickDelete={onClickFileDelete}
                onClickReset={onClickFileReset}
                disabledAdd={isDocumentsReadOnly}
                disabledDelete={isDocumentsReadOnly}
                disabledReset={isDocumentsReadOnly}
            />
        );
    }

    return (
        <>
            {pafId &&
                <>
                    <FormPaper elevation={4}>
                        {/* Form Title*/}
                        <FormTitle
                            title={aevMode === "add"
                                ? "PAF: New"
                                : "PAF: Update"}
                            titleIcon={"assignment_rounded_icon"}
                        />

                        {/* Status Banner - Show when PAF is Approved or Rejected */}
                        {(isApproved || signatureStatus === 'REJECTED') && (
                            <Box sx={{ px: 3, pt: 2, pb: 0, mb: 2 }}>
                                <Alert
                                    severity={isApproved ? "success" : "error"}
                                    sx={{
                                        borderRadius: 1,
                                        '& .MuiAlert-icon': {
                                            fontSize: 28
                                        }
                                    }}
                                >
                                    <AlertTitle sx={{ fontWeight: 600, mb: 0.5 }}>
                                        {isApproved ? "PAF Approved" : "PAF Rejected"}
                                    </AlertTitle>
                                    <Box sx={{ fontSize: '0.95rem' }}>
                                        {isApproved
                                            ? "This PAF has been approved and is now active."
                                            : (
                                                <>
                                                    This PAF has been rejected for the following reason:
                                                    {licenseeSignatureRejectionReason && licenseeSignatureRejectionReason.trim() && (
                                                        <Box sx={{ mt: 1 }}>

                                                            {licenseeSignatureRejectionReason.length > 100 ? (
                                                                <ShowMore
                                                                    text={licenseeSignatureRejectionReason}
                                                                    truncatedLength={100}
                                                                    showMoreText="Show more >>"
                                                                    showLessText="<< Show less"
                                                                    typeVariant="body2"
                                                                />
                                                            ) : (
                                                                <Typography component="span" variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                                    {licenseeSignatureRejectionReason}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    )}
                                                </>
                                            )
                                        }
                                    </Box>
                                </Alert>
                            </Box>
                        )}

                        <Grid container>
                            {showProgress ? (
                                <Grid container justifyContent={"center"} item>
                                    <ProgressPanel title={progressTitle} />
                                </Grid>
                            ) : (showError ? (
                                <Grid container justifyContent={"center"} item>
                                    <MessagePanel
                                        {...msgPanelProps}
                                    />
                                </Grid>
                            ) : (
                                <Grid item xs={12}>
                                    <Form
                                        autoComplete="off"
                                        onSubmit={handleSubmit(onSubmit, onSubmitErrors)}
                                    >
                                        <AevTabs
                                            value={selTabIndex}
                                            onChange={handleTabChange}
                                            variant="scrollable"
                                            scrollButtons="auto"
                                            allowScrollButtonsMobile
                                        >
                                            <AevTab label="Company" />
                                            <AevTab label="Address" />
                                            <AevTab label="Contact" />
                                            <AevTab label="Broker" />
                                            <AevTab label="Licensee" />
                                            <AevTab label="Signatures" />
                                            <AevTab label="Notes" />
                                            {aevMode !== "add" && <AevTab label="Documents" />}
                                        </AevTabs>
                                        <Box sx={{ minHeight: theme.spacing(73) }}>
                                            {/* Paf Info Tab */}
                                            <DaTabPanel selectedIndex={selTabIndex} index={0}>
                                                <Box sx={{ pointerEvents: isCompanyReadOnly ? 'none' : 'auto' }}>
                                                    <Grid container spacing={theme.spacing(1)}>
                                                        {/* PAF Section Header */}
                                                        <Grid item xs={12} md={12}>
                                                            <FormSectionHeader
                                                                title={"PAF Info"}
                                                                typeVariant={"h5"}
                                                            />
                                                        </Grid>
                                                        {userHasPrivilege([AuthRolesEnum.Licensee, AuthRolesEnum.BrokerAgent, AuthRolesEnum.SystemAdmin]) && (
                                                            <Grid item xs={12} md={4} display={"flex"} alignItems={"end"} justifyContent={"start"}>
                                                                <DaTextField
                                                                    control={control}
                                                                    name={pafAEVFields.pafSid.name}
                                                                    label={aevMode === "add" ? "Company SID" : "PAF SID"}
                                                                    variant={formFieldVariant}
                                                                    size={formFieldSize}
                                                                    errors={errors}
                                                                />
                                                                {/* <DaButton buttonStyle="secondary"
                                                            sx={{ width: theme.spacing(21), ml: 1 }}
                                                            onClick={() => {

                                                                alert("Form IsValid: " + isValid);

                                                            }}
                                                        >
                                                            Validate
                                                        </DaButton> */}
                                                            </Grid>

                                                        )}
                                                        <Grid item xs={12} md={4}>
                                                            {
                                                                aevMode === "add" && userHasPrivilege([AuthRolesEnum.Licensee, AuthRolesEnum.SystemAdmin]) && (
                                                                    <CompanySelection
                                                                        setValue={setValue}
                                                                        naicsSearch={naicsSearch}
                                                                        stateSearch={stateSearch}
                                                                        setAuthorizedUsers={setAuthorizedUsers}
                                                                    />)



                                                            }
                                                        </Grid>
                                                        <Grid item xs={12} md={6}>
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                            <DaTextField
                                                                control={control}
                                                                name={pafAEVFields.companyName.name}
                                                                label={pafAEVFields.companyName.label}
                                                                required={pafAEVFields.companyName.required}
                                                                readOnly={isCompanyReadOnly}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                            <DaTextField
                                                                control={control}
                                                                name={pafAEVFields.parentCompanyName.name}
                                                                label={pafAEVFields.parentCompanyName.label}
                                                                required={pafAEVFields.parentCompanyName.required}
                                                                readOnly={isCompanyReadOnly}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                            <DaTextField
                                                                control={control}
                                                                name={pafAEVFields.dbaName.name}
                                                                label={pafAEVFields.dbaName.label}
                                                                required={pafAEVFields.dbaName.required}
                                                                readOnly={isCompanyReadOnly}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <DaAutocompleteField
                                                                control={control}
                                                                name={pafAEVFields.naicsCode.name}
                                                                label={pafAEVFields.naicsCode.label}
                                                                required={pafAEVFields.naicsCode.required}
                                                                readOnly={isCompanyReadOnly}
                                                                options={naicsOptions.map(({ naics_code, naics_title }) =>
                                                                    ({ value: naics_code, label: `${naics_code} - ${naics_title}` })
                                                                )}
                                                                onInputChange={naicsInputChanged}
                                                                noOptionsText="Type your industry category to find the NAICS"
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <DaNumberFormatField
                                                                control={control}
                                                                name={pafAEVFields.uspsMailerId.name}
                                                                label={pafAEVFields.uspsMailerId.label}
                                                                required={pafAEVFields.uspsMailerId.required}
                                                                readOnly={isCompanyReadOnly}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                                numberFormat={NumberFormatType.MAILER_ID}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <DaNumberFormatField
                                                                control={control}
                                                                name={pafAEVFields.customerRegistrationId.name}
                                                                label={pafAEVFields.customerRegistrationId.label}
                                                                required={pafAEVFields.customerRegistrationId.required}
                                                                readOnly={isCompanyReadOnly}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                                numberFormat={NumberFormatType.CR_ID}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <DaCheckboxField
                                                                control={control}
                                                                name={pafAEVFields.isForeignLocation.name}
                                                                label={pafAEVFields.isForeignLocation.label}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                                disabled={isCompanyReadOnly}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <DaCheckboxField
                                                                control={control}
                                                                name={pafAEVFields.isNcoa.name}
                                                                label={<>NCOA<sup>Link</sup> Service</>}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                                disabled={isCompanyReadOnly}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <DaCheckboxField
                                                                control={control}
                                                                name={pafAEVFields.isDsf.name}
                                                                label={<>DSF<sup>2</sup> Service</>}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                                disabled={isCompanyReadOnly}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <DaCheckboxField
                                                                control={control}
                                                                name={pafAEVFields.isTriplex.name}
                                                                label={pafAEVFields.isTriplex.label}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                                disabled={isCompanyReadOnly}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <DaCheckboxField
                                                                control={control}
                                                                name={pafAEVFields.isWaiverOnFile.name}
                                                                label={<>Waive NCOA<sup>Link</sup> 7-Day Fulfillment</>}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                                disabled={isCompanyReadOnly}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <DaCheckboxField
                                                                control={control}
                                                                name={pafAEVFields.customerIsInternal.name}
                                                                label={pafAEVFields.customerIsInternal.label}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                                disabled={isCompanyReadOnly}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </DaTabPanel>
                                            {/* Address Tab */}
                                            <DaTabPanel selectedIndex={selTabIndex} index={1}>
                                                <Box sx={{ pointerEvents: isAddressReadOnly ? 'none' : 'auto' }}>
                                                    <Grid container spacing={theme.spacing(1)}>
                                                        <Grid item xs={12} md={12}>
                                                            <FormSectionHeader
                                                                title={"Address Details"}
                                                                typeVariant={"h5"}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                            <DaTextField
                                                                control={control}
                                                                name={pafAEVFields.address1.name}
                                                                label={pafAEVFields.address1.label}
                                                                required={pafAEVFields.address1.required}
                                                                readOnly={isAddressReadOnly}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                            <DaTextField
                                                                control={control}
                                                                name={pafAEVFields.address2.name}
                                                                label={pafAEVFields.address2.label}
                                                                required={pafAEVFields.address2.required}
                                                                readOnly={isAddressReadOnly}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={6}>
                                                            <DaTextField
                                                                control={control}
                                                                name={pafAEVFields.city.name}
                                                                label={pafAEVFields.city.label}
                                                                required={pafAEVFields.city.required}
                                                                readOnly={isAddressReadOnly}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <DaAutocompleteField
                                                                control={control}
                                                                name={pafAEVFields.state.name}
                                                                label={pafAEVFields.state.label}
                                                                required={pafAEVFields.state.required}
                                                                readOnly={isAddressReadOnly}
                                                                options={stateOptions.map(
                                                                    ({ state_code, state_name, country_code }) =>
                                                                        ({ value: state_code, label: `${state_code} - ${state_name} (${country_code})` })
                                                                )}
                                                                onInputChange={stateInputChanged}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={2}>
                                                            <DaTextField
                                                                control={control}
                                                                name={pafAEVFields.postalCode.name}
                                                                label={pafAEVFields.postalCode.label}
                                                                required={pafAEVFields.postalCode.required}
                                                                readOnly={isAddressReadOnly}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={6}>
                                                            <DaTextField
                                                                control={control}
                                                                name={pafAEVFields.urbanization.name}
                                                                label={pafAEVFields.urbanization.label}
                                                                required={pafAEVFields.urbanization.required}
                                                                readOnly={isAddressReadOnly}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={6}>
                                                            <HTMLComment text={"Placeholder"} />
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </DaTabPanel>
                                            {/* Contact Tab */}
                                            <DaTabPanel selectedIndex={selTabIndex} index={2}>
                                                <Box sx={{ pointerEvents: isContactReadOnly ? 'none' : 'auto' }}>
                                                    <Grid container spacing={theme.spacing(1)}>
                                                        <Grid item xs={12} md={12}>
                                                            <FormSectionHeader
                                                                title={"Contact Info"}
                                                                typeVariant={"h5"}
                                                            />
                                                        </Grid>
                                                        {authorizedUsers.length > 0 && (
                                                            <Grid item xs={12} md={5}>
                                                                <DaAutocompleteField
                                                                    control={control}
                                                                    name={"representative_selection" as any}
                                                                    label="Change Representative"
                                                                    readOnly={isContactReadOnly}
                                                                    options={authorizedUsers.map((user) => ({
                                                                        value: user.email,
                                                                        label: `${user.first_name} ${user.last_name} (${user.email})`
                                                                    }))}
                                                                    variant={formFieldVariant}
                                                                    size={formFieldSize}
                                                                    errors={errors}
                                                                />
                                                            </Grid>
                                                        )}
                                                        <Grid item xs={12} md={12}>
                                                            <DaTextField
                                                                control={control}
                                                                name={pafAEVFields.representativeName.name}
                                                                label={pafAEVFields.representativeName.label}
                                                                required={pafAEVFields.representativeName.required}
                                                                readOnly={isContactReadOnly || isContactReadyOnlyInAddMode}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                            <DaTextField
                                                                control={control}
                                                                name={pafAEVFields.representativeTitle.name}
                                                                label={pafAEVFields.representativeTitle.label}
                                                                required={pafAEVFields.representativeTitle.required}
                                                                readOnly={isContactReadOnly}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                            <DaTextField
                                                                control={control}
                                                                name={pafAEVFields.representativeEmail.name}
                                                                label={pafAEVFields.representativeEmail.label}
                                                                required={pafAEVFields.representativeEmail.required}
                                                                readOnly={isContactReadOnly || isContactReadyOnlyInAddMode}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                            <DaTextField
                                                                control={control}
                                                                name={pafAEVFields.companyWebsite.name}
                                                                label={pafAEVFields.companyWebsite.label}
                                                                required={pafAEVFields.companyWebsite.required}
                                                                readOnly={isContactReadOnly || isContactReadyOnlyInAddMode}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <DaNumberFormatField
                                                                control={control}
                                                                name={pafAEVFields.phone.name}
                                                                label={pafAEVFields.phone.label}
                                                                required={pafAEVFields.phone.required}
                                                                readOnly={isContactReadOnly || isContactReadyOnlyInAddMode}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                                numberFormat={NumberFormatType.US_Phone}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </DaTabPanel>
                                            {/* Broker Tab */}
                                            <DaTabPanel selectedIndex={selTabIndex} index={3}>
                                                <Box sx={{ pointerEvents: isBrokerTabReadOnly ? 'none' : 'auto' }}>
                                                    <Grid container spacing={theme.spacing(1)}>
                                                        <Grid item xs={12} md={12}>
                                                            <FormSectionHeader
                                                                title={"Broker"}
                                                                typeVariant={"h5"}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <DaTextField
                                                                control={control}
                                                                name={pafAEVFields.listAdminSid.name}
                                                                label={pafAEVFields.listAdminSid.label}
                                                                readOnly
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                                key={watch('listadmin__listadmin_sid' as any)}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={6}>
                                                            <Box sx={{ pointerEvents: isEditMode ? 'auto' : 'inherit' }}>
                                                                {
                                                                    userHasPrivilege([AuthRolesEnum.BrokerAgent, AuthRolesEnum.ListAdmin,
                                                                    AuthRolesEnum.ListOwner, AuthRolesEnum.Licensee, AuthRolesEnum.SystemAdmin]) && (
                                                                        <BrokerSelection
                                                                            setValue={setValue}
                                                                            onSaveBeforeNavigate={saveBeforeNavigate}
                                                                            pafId={pafId}
                                                                            customerId={watch('customer_id' as any)}
                                                                            context="paf"
                                                                        />
                                                                    )

                                                                }
                                                            </Box>
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                            <DaTextField
                                                                control={control}
                                                                name={pafAEVFields.listAdminCompanyName.name}
                                                                label={pafAEVFields.listAdminCompanyName.label}
                                                                readOnly
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                                key={watch('listadmin__company_name' as any)}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                            <DaTextField
                                                                control={control}
                                                                name={pafAEVFields.listAdminAddress1.name}
                                                                label={pafAEVFields.listAdminAddress1.label}
                                                                readOnly
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                                key={watch('listadmin__address1' as any)}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                            <DaTextField
                                                                control={control}
                                                                name={pafAEVFields.listAdminAddress2.name}
                                                                label={pafAEVFields.listAdminAddress2.label}
                                                                readOnly
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                                key={watch('listadmin__address2' as any)}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={6}>
                                                            <DaTextField
                                                                control={control}
                                                                name={pafAEVFields.listAdminCity.name}
                                                                label={pafAEVFields.listAdminCity.label}
                                                                readOnly
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                                key={watch('listadmin__city' as any)}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <DaTextField
                                                                control={control}
                                                                name={pafAEVFields.listAdminState.name}
                                                                label={pafAEVFields.listAdminState.label}
                                                                readOnly
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                                key={watch('listadmin__state' as any)}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={2}>
                                                            <DaTextField
                                                                control={control}
                                                                name={pafAEVFields.listAdminPostalCode.name}
                                                                label={pafAEVFields.listAdminPostalCode.label}
                                                                readOnly
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                                key={watch('listadmin__postal_code' as any)}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <DaTextField
                                                                control={control}
                                                                name={pafAEVFields.listAdminNaicsCode.name}
                                                                label={pafAEVFields.listAdminNaicsCode.label}
                                                                readOnly
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                                key={watch('listadmin__naics_code' as any)}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <DaTextField
                                                                control={control}
                                                                name={pafAEVFields.listAdminPhone.name}
                                                                label={pafAEVFields.listAdminPhone.label}
                                                                readOnly
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                                key={watch('listadmin__phone' as any)}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                            <DaTextField
                                                                control={control}
                                                                name={pafAEVFields.listAdminRepresentativeName.name}
                                                                label={pafAEVFields.listAdminRepresentativeName.label}
                                                                readOnly
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                                key={watch('listadmin__representative_name')}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                            <DaTextField
                                                                control={control}
                                                                name={pafAEVFields.listAdminRepresentativeTitle.name}
                                                                label={pafAEVFields.listAdminRepresentativeTitle.label}
                                                                readOnly
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                                key={watch('listadmin__representative_title')}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                            <DaTextField
                                                                control={control}
                                                                name={pafAEVFields.listAdminRepresentativeEmail.name}
                                                                label={pafAEVFields.listAdminRepresentativeEmail.label}
                                                                readOnly
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                                key={watch('listadmin__representative_email')}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                            <DaHiddenField
                                                                control={control}
                                                                name={pafAEVFields.listAdminId.name}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </DaTabPanel>
                                            {/* Licensee Tab */}
                                            <DaTabPanel selectedIndex={selTabIndex} index={4}>
                                                <Box sx={{ pointerEvents: isViewMode && !canEditLicensee ? 'none' : 'auto' }}>
                                                    <Grid container spacing={theme.spacing(1)}>
                                                        <Grid item xs={12} md={12}>
                                                            <FormSectionHeader
                                                                title={"Licensee"}
                                                                typeVariant={"h5"}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                            <DaTextField
                                                                control={control}
                                                                name={pafAEVFields.licenseeOfficerName.name}
                                                                label={pafAEVFields.licenseeOfficerName.label}
                                                                readOnly={(isViewMode && !canEditLicensee) || !isLicenseeUser}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                            <DaTextField
                                                                control={control}
                                                                name={pafAEVFields.licenseeOfficerTitle.name}
                                                                label={pafAEVFields.licenseeOfficerTitle.label}
                                                                readOnly={(isViewMode && !canEditLicensee) || !isLicenseeUser}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <DaNumberFormatField
                                                                control={control}
                                                                name={pafAEVFields.licenseeFederalTaxId.name}
                                                                label={pafAEVFields.licenseeFederalTaxId.label}
                                                                readOnly={(isViewMode && !canEditLicensee) || !isLicenseeUser}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                errors={errors}
                                                                numberFormat={NumberFormatType.EIN}
                                                            />
                                                        </Grid>
                                                        {userHasPrivilege([AuthRolesEnum.Licensee, AuthRolesEnum.SystemAdmin]) &&
                                                            <>
                                                                <Grid item xs={12} md={12}>
                                                                    <DaTextField
                                                                        control={control}
                                                                        name={pafAEVFields.licenseeAcctExec.name}
                                                                        label={pafAEVFields.licenseeAcctExec.label}
                                                                        required={pafAEVFields.licenseeAcctExec.required}
                                                                        readOnly={isViewMode && !canEditLicensee}
                                                                        variant={formFieldVariant}
                                                                        size={formFieldSize}
                                                                        errors={errors}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} md={12}>
                                                                    <DaTextField
                                                                        control={control}
                                                                        name={pafAEVFields.licenseeSalesOffice.name}
                                                                        label={pafAEVFields.licenseeSalesOffice.label}
                                                                        required={pafAEVFields.licenseeSalesOffice.required}
                                                                        readOnly={isViewMode && !canEditLicensee}
                                                                        variant={formFieldVariant}
                                                                        size={formFieldSize}
                                                                        errors={errors}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} md={12}>
                                                                    <DaTextField
                                                                        control={control}
                                                                        name={pafAEVFields.licenseeOperator.name}
                                                                        label={pafAEVFields.licenseeOperator.label}
                                                                        required={pafAEVFields.licenseeOperator.required}
                                                                        readOnly={isViewMode && !canEditLicensee}
                                                                        variant={formFieldVariant}
                                                                        size={formFieldSize}
                                                                        errors={errors}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} md={12}>
                                                                    <DaHiddenField
                                                                        control={control}
                                                                        name={pafAEVFields.licenseeId.name}
                                                                        errors={errors}
                                                                    />
                                                                </Grid>
                                                            </>
                                                        }
                                                    </Grid>
                                                </Box>
                                            </DaTabPanel>
                                            {/* Signatures Tab */}
                                            <DaTabPanel selectedIndex={selTabIndex} index={5}>
                                                <Box sx={{ pointerEvents: 'auto' }}>
                                                    <Grid container spacing={theme.spacing(1)}>
                                                        <Grid item xs={12} md={12}>
                                                            <FormSectionHeader
                                                                title={"Signatures"}
                                                                typeVariant={"h5"}
                                                            />
                                                        </Grid>

                                                        {/* List Owner Signature Section */}
                                                        <Grid item xs={12} md={12}>
                                                            <Box sx={{
                                                                mt: 2,
                                                                p: 2,
                                                                border: '1px solid',
                                                                borderColor: 'divider',
                                                                borderRadius: 1,
                                                                backgroundColor: 'background.paper'
                                                            }}>
                                                                <Grid container spacing={theme.spacing(1)}>
                                                                    <Grid item xs={12} md={12}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            <FormSectionHeader
                                                                                title={"List Owner Signature"}
                                                                                typeVariant={"h6"}
                                                                            />
                                                                            <SignatureStatusIndicator isSigned={isListOwnerSignaturePresent} />
                                                                        </Box>
                                                                    </Grid>
                                                                    <Grid item xs={12} md={4}>
                                                                        <DaTextField
                                                                            control={control}
                                                                            name={pafAEVFields.representativeName.name}
                                                                            label={pafAEVFields.representativeName.label}
                                                                            readOnly
                                                                            variant={formFieldVariant}
                                                                            size={formFieldSize}
                                                                            errors={errors}
                                                                            key={watch('company__representative_name' as any)}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={12} md={4}>
                                                                        <DaSignatureField
                                                                            control={control}
                                                                            name={pafAEVFields.listOwnerSignatureText.name}
                                                                            label={pafAEVFields.listOwnerSignatureText.label}
                                                                            readOnly={true}
                                                                            variant={formFieldVariant}
                                                                            size={formFieldSize}
                                                                            errors={errors}
                                                                            key={watch('list_owner_signature_text' as any)}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={12} md={4}>
                                                                        <DaDateTimePickerField
                                                                            control={control}
                                                                            name={pafAEVFields.listOwnerSignatureDate.name}
                                                                            label={pafAEVFields.listOwnerSignatureDate.label}
                                                                            readOnly={true}
                                                                            variant={formFieldVariant}
                                                                            size={formFieldSize}
                                                                            disableOpenPicker={true}
                                                                            errors={errors}
                                                                            key={watch('list_owner_signature_date' as any)}
                                                                        />
                                                                        <DaHiddenField
                                                                            control={control}
                                                                            name={pafAEVFields.listOwnerSignatureAttest.name}
                                                                            errors={errors}
                                                                        />
                                                                    </Grid>
                                                                    {/* Show EsignSelection for ListOwner in edit mode if pending and status is WAITING_SIGNATURES */}
                                                                    {isEditMode && signatureStatus === 'WAITING_SIGNATURES' && isListOwnerPending && userHasPrivilege([AuthRolesEnum.ListOwner]) && (
                                                                        <Grid item xs={12} md={12}>
                                                                            <EsignSelection
                                                                                getValues={getValues}
                                                                                setValue={setValue}
                                                                                resetField={resetField}
                                                                                source={"listOwner"}
                                                                            />
                                                                        </Grid>
                                                                    )}
                                                                    {/* View mode: show EsignSelection for ListOwner if pending_signatures includes list_owner and user is ListOwner and email matches */}
                                                                    {!isEditMode && isViewMode && signatureStatus === 'WAITING_SIGNATURES' && isListOwnerPending && userHasPrivilege([AuthRolesEnum.ListOwner]) && isRepresentativeEmailMatch && (
                                                                        <Grid item xs={12} md={12}>
                                                                            <EsignSelection
                                                                                getValues={getValues}
                                                                                setValue={setValue}
                                                                                resetField={resetField}
                                                                                source={"listOwner"}
                                                                            />
                                                                        </Grid>
                                                                    )}
                                                                    {isAddMode && userHasPrivilege([AuthRolesEnum.ListOwner]) && isRepresentativeEmailMatch && (
                                                                        <Grid item xs={12} md={12}>
                                                                            <EsignSelection
                                                                                getValues={getValues}
                                                                                setValue={setValue}
                                                                                resetField={resetField}
                                                                                source={"listOwner"}
                                                                            />
                                                                        </Grid>
                                                                    )}
                                                                </Grid>
                                                            </Box>
                                                        </Grid>

                                                        {/* List Admin / Broker Signature Section */}
                                                        <Grid item xs={12} md={12}>
                                                            <Box sx={{
                                                                mt: 2,
                                                                p: 2,
                                                                border: '1px solid',
                                                                borderColor: 'divider',
                                                                borderRadius: 1,
                                                                backgroundColor: 'background.paper'
                                                            }}>
                                                                <Grid container spacing={theme.spacing(1)}>
                                                                    <Grid item xs={12} md={12}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            <FormSectionHeader
                                                                                title={"Broker / List Admin Signature"}
                                                                                typeVariant={"h6"}
                                                                            />
                                                                            {hasBrokerSid && (
                                                                                <SignatureStatusIndicator isSigned={isListAdminSignaturePresent} />
                                                                            )}
                                                                        </Box>
                                                                    </Grid>
                                                                    <Grid item xs={12} md={4}>
                                                                        <DaTextField
                                                                            control={control}
                                                                            name={pafAEVFields.listAdminRepresentativeName.name}
                                                                            label={pafAEVFields.listAdminRepresentativeName.label}
                                                                            readOnly
                                                                            variant={formFieldVariant}
                                                                            size={formFieldSize}
                                                                            errors={errors}
                                                                            key={watch('listadmin__representative_name' as any)}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={12} md={4}>
                                                                        <DaSignatureField
                                                                            control={control}
                                                                            name={pafAEVFields.listAdminSignatureText.name}
                                                                            label={pafAEVFields.listAdminSignatureText.label}
                                                                            readOnly={true}
                                                                            variant={formFieldVariant}
                                                                            size={formFieldSize}
                                                                            errors={errors}
                                                                            key={watch('list_admin_signature_text' as any)}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={12} md={4}>
                                                                        <DaDateTimePickerField
                                                                            control={control}
                                                                            name={pafAEVFields.listAdminSignatureDate.name}
                                                                            label={pafAEVFields.listAdminSignatureDate.label}
                                                                            readOnly={true}
                                                                            variant={formFieldVariant}
                                                                            size={formFieldSize}
                                                                            disableOpenPicker={true}
                                                                            errors={errors}
                                                                            key={watch('list_admin_signature_date' as any)}
                                                                        />
                                                                    </Grid>
                                                                    {/* Show EsignSelection for Broker/ListAdmin in edit mode if pending and status is WAITING_SIGNATURES */}
                                                                    {isEditMode && signatureStatus === 'WAITING_SIGNATURES' && userHasPrivilege([AuthRolesEnum.BrokerAgent, AuthRolesEnum.ListAdmin]) && (
                                                                        <Grid item xs={12} md={12}>
                                                                            <EsignSelection
                                                                                getValues={getValues}
                                                                                setValue={setValue}
                                                                                resetField={resetField}
                                                                                source={"listAdmin"}
                                                                            />
                                                                        </Grid>
                                                                    )}
                                                                    {/* View mode: show EsignSelection for Broker/ListAdmin if pending_signatures includes broker and user is BrokerAgent or ListAdmin and email matches */}
                                                                    {!isEditMode && isViewMode && signatureStatus === 'WAITING_SIGNATURES' && isBrokerPending && userHasPrivilege([AuthRolesEnum.BrokerAgent, AuthRolesEnum.ListAdmin]) && isBrokerEmailMatch && (
                                                                        <Grid item xs={12} md={12}>
                                                                            <EsignSelection
                                                                                getValues={getValues}
                                                                                setValue={setValue}
                                                                                resetField={resetField}
                                                                                source={"listAdmin"}
                                                                            />
                                                                        </Grid>
                                                                    )}
                                                                </Grid>
                                                            </Box>
                                                        </Grid>

                                                        {/* Licensee Signature Section */}
                                                        {showLicenseeSignatureSection && (
                                                            <Grid item xs={12} md={12}>
                                                                <Box sx={{
                                                                    mt: 2,
                                                                    p: 2,
                                                                    border: '1px solid',
                                                                    borderColor: 'divider',
                                                                    borderRadius: 1,
                                                                    backgroundColor: 'background.paper'
                                                                }}>
                                                                    <Grid container spacing={theme.spacing(1)}>
                                                                        <Grid item xs={12} md={12}>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                <FormSectionHeader
                                                                                    title={"Licensee Signature"}
                                                                                    typeVariant={"h6"}
                                                                                />
                                                                                <SignatureStatusIndicator isSigned={isLicenseeSignaturePresent} />
                                                                            </Box>
                                                                        </Grid>
                                                                        <Grid item xs={12} md={4}>
                                                                            <DaTextField
                                                                                control={control}
                                                                                name={pafAEVFields.licenseeOfficerName.name}
                                                                                label={pafAEVFields.licenseeOfficerName.label}
                                                                                readOnly
                                                                                variant={formFieldVariant}
                                                                                size={formFieldSize}
                                                                                errors={errors}
                                                                                key={watch('licensee__officer_name' as any)}
                                                                            />
                                                                        </Grid>
                                                                        <Grid item xs={12} md={4}>
                                                                            <DaSignatureField
                                                                                control={control}
                                                                                name={pafAEVFields.licenseeSignatureText.name}
                                                                                label={pafAEVFields.licenseeSignatureText.label}
                                                                                readOnly={true}
                                                                                variant={formFieldVariant}
                                                                                size={formFieldSize}
                                                                                errors={errors}
                                                                                key={watch('licensee_signature_text' as any)}
                                                                            />
                                                                        </Grid>
                                                                        <Grid item xs={12} md={4}>
                                                                            <DaDateTimePickerField
                                                                                control={control}
                                                                                name={pafAEVFields.licenseeSignatureDate.name}
                                                                                label={pafAEVFields.licenseeSignatureDate.label}
                                                                                readOnly={true}
                                                                                variant={formFieldVariant}
                                                                                size={formFieldSize}
                                                                                disableOpenPicker={true}
                                                                                errors={errors}
                                                                                key={watch('licensee_signature_date' as any)}
                                                                            />
                                                                            <DaHiddenField
                                                                                control={control}
                                                                                name={pafAEVFields.licenseeSignatureAttest.name}
                                                                                errors={errors}
                                                                            />
                                                                        </Grid>
                                                                        {/* Hide e-sign for Licensee users as signature is auto-filled */}
                                                                        {!isApproved && !isWaitingApproval && !isPendingWithBroker &&
                                                                            !(userHasPrivilege([AuthRolesEnum.Licensee]) && !userHasPrivilege([AuthRolesEnum.SystemAdmin])) && (
                                                                                <Grid item xs={12} md={12}>
                                                                                    <EsignSelection
                                                                                        getValues={getValues}
                                                                                        setValue={setValue}
                                                                                        resetField={resetField}
                                                                                        source={"licensee"}
                                                                                    />
                                                                                </Grid>
                                                                            )}
                                                                    </Grid>
                                                                </Box>
                                                            </Grid>
                                                        )}
                                                    </Grid>
                                                </Box>
                                            </DaTabPanel>
                                            {/* Notes Tab */}
                                            <DaTabPanel selectedIndex={selTabIndex} index={6}>
                                                <Box sx={{ pointerEvents: isNotesReadOnly ? 'none' : 'auto' }}>
                                                    <Grid container spacing={theme.spacing(1)}>
                                                        <Grid item xs={12} md={12}>
                                                            <FormSectionHeader
                                                                title={"Additional Notes"}
                                                                typeVariant={"h5"}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                            <DaTextField
                                                                control={control}
                                                                name={pafAEVFields.notes.name}
                                                                label={pafAEVFields.notes.label}
                                                                required={pafAEVFields.notes.required}
                                                                readOnly={isNotesReadOnly}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                multiline
                                                                rows={4}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                            <DaTextField
                                                                control={control}
                                                                name={pafAEVFields.comments.name}
                                                                label={pafAEVFields.comments.label}
                                                                required={pafAEVFields.comments.required}
                                                                readOnly={isNotesReadOnly}
                                                                variant={formFieldVariant}
                                                                size={formFieldSize}
                                                                multiline
                                                                rows={4}
                                                                errors={errors}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                            <HTMLComment text={"Placeholder"} />
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </DaTabPanel>
                                            {/* Documents Tab - in view mode allow download via grid link when documents present; toolbar Add/Delete/Reset stay disabled via isDocumentsReadOnly */}
                                            <DaTabPanel selectedIndex={selTabIndex} index={7}>
                                                <Box>
                                                    <Grid container spacing={theme.spacing(1)}>
                                                        <Grid item xs={12} md={12}>
                                                            <FormSectionHeader
                                                                title={"Documents"}
                                                                typeVariant={"h5"}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={12}>
                                                            <DaGridContainer
                                                                sx={{
                                                                    height: 430,
                                                                    width: '100%',
                                                                }}
                                                            >
                                                                <DataGrid
                                                                    components={{
                                                                        LoadingOverlay: DaGridLoadingOverlay,
                                                                        Toolbar: filesGridToolbar,
                                                                    }}
                                                                    initialState={{
                                                                        pagination: {
                                                                            page: 0,
                                                                            pageSize: DaGridSettings.defaultPageSize,
                                                                        },
                                                                    }}
                                                                    loading={isFilesLoading}
                                                                    columns={pafFilesColumns}
                                                                    rows={pafFilesData.rows}
                                                                    rowCount={pafFilesData.row_count}
                                                                    getRowId={(row) => row.file_id}
                                                                    rowsPerPageOptions={DaGridSettings.rowsPerPageOptions}
                                                                    headerHeight={DaGridSettings.headerRowHeight}
                                                                    rowHeight={DaGridSettings.rowHeight}
                                                                    checkboxSelection={!isDocumentsReadOnly}
                                                                    disableSelectionOnClick
                                                                    selectionModel={filesSelectionModel}
                                                                    onSelectionModelChange={(newSelection) => {
                                                                        setFilesSelectionModel(newSelection);
                                                                    }}
                                                                />
                                                            </DaGridContainer>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </DaTabPanel>
                                        </Box>
                                        <Grid container spacing={theme.spacing(1)}>
                                            <Grid item xs={12} md={12}>
                                                <ButtonBar buttonAlignment="alignRight">
                                                    {(() => {
                                                        // KISS principle: define tab boundaries and button visibility
                                                        const isAdd = aevMode === "add";
                                                        const lastTabIdx = isAdd ? 6 : 7; // Notes or Documents
                                                        const isFirstTab = selTabIndex === 0;
                                                        const isLastTab = selTabIndex === lastTabIdx;
                                                        // EXPIRED status: show only Cancel button (view only mode)
                                                        if (isExpired) {
                                                            return (
                                                                <>
                                                                    <DaButton buttonStyle="secondary" onClick={handleCancelClick}>Cancel</DaButton>
                                                                    {!isLastTab && (
                                                                        <>
                                                                            {!isFirstTab && (
                                                                                <DaButton buttonStyle="secondary" onClick={handlePrevious}>Previous</DaButton>
                                                                            )}
                                                                            <DaButton buttonStyle="primary" type="button" onClick={e => { e.preventDefault(); e.stopPropagation(); handleNext(); }}>Next</DaButton>
                                                                        </>
                                                                    )}
                                                                </>
                                                            );
                                                        }
                                                        if (isApproved || isRejected || isExpired) {
                                                            return (
                                                                <>
                                                                    <DaButton buttonStyle="secondary" onClick={() => navigate("/pafs")}>Close</DaButton>
                                                                    {!isLastTab && (
                                                                        <>
                                                                            {!isFirstTab && (
                                                                                <DaButton buttonStyle="secondary" onClick={handlePrevious}>Previous</DaButton>
                                                                            )}
                                                                            <DaButton buttonStyle="primary" type="button" onClick={e => { e.preventDefault(); e.stopPropagation(); handleNext(); }}>Next</DaButton>
                                                                        </>
                                                                    )}
                                                                </>
                                                            );
                                                        }
                                                        // Not approved
                                                        if (isAdd) {
                                                            return (
                                                                <>
                                                                    <DaButton buttonStyle="secondary" onClick={handleCancelClick}>Cancel</DaButton>
                                                                    {!isFirstTab && <DaButton buttonStyle="secondary" onClick={handlePrevious}>Previous</DaButton>}
                                                                    {!isLastTab && <DaButton buttonStyle="primary" type="button" onClick={e => { e.preventDefault(); e.stopPropagation(); handleNext(); }}>Next</DaButton>}
                                                                    {isLastTab && <DaButton buttonStyle="primary" type="submit" sx={{ ml: 1 }}>Save and Submit</DaButton>}
                                                                </>
                                                            );
                                                        }
                                                        // Update/view mode
                                                        if (!isLastTab) {
                                                            return (
                                                                <>
                                                                    <DaButton buttonStyle="secondary" onClick={handleCancelClick}>Cancel</DaButton>
                                                                    {!isFirstTab && <DaButton buttonStyle="secondary" onClick={handlePrevious}>Previous</DaButton>}
                                                                    <DaButton buttonStyle="primary" type="button" onClick={e => { e.preventDefault(); e.stopPropagation(); handleNext(); }}>Next</DaButton>
                                                                </>
                                                            );
                                                        }
                                                        // Last tab in update/view mode
                                                        // Show Save and Submit in view mode only if e-Sign is shown:
                                                        // 1. pending_signatures includes "list_owner" AND user is ListOwner AND representative email matches
                                                        // 2. pending_signatures includes "broker" AND user is BrokerAgent or ListAdmin AND broker email matches
                                                        const showEsignForListOwner = isViewMode && signatureStatus === 'WAITING_SIGNATURES' && isListOwnerPending && userHasPrivilege([AuthRolesEnum.ListOwner]) && isRepresentativeEmailMatch;
                                                        const showEsignForBroker = isViewMode && signatureStatus === 'WAITING_SIGNATURES' && isBrokerPending && userHasPrivilege([AuthRolesEnum.BrokerAgent, AuthRolesEnum.ListAdmin]) && isBrokerEmailMatch;
                                                        const showEsignSaveSubmit = showEsignForListOwner || showEsignForBroker;
                                                        return (
                                                            <>
                                                                <DaButton buttonStyle="secondary" onClick={handleCancelClick}>Cancel</DaButton>
                                                                {canEditLicensee ? (
                                                                    <>
                                                                        <DaButton buttonStyle="secondary" onClick={() => { dlgConfirm_Show('reject'); }} disabled={!canApproveReject}>Reject</DaButton>
                                                                        <DaButton buttonStyle="primary" onClick={onApprove} disabled={!canApproveReject}>Approve</DaButton>
                                                                    </>
                                                                ) : (
                                                                    (userHasPrivilege([
                                                                        AuthRolesEnum.ListAdmin,
                                                                        AuthRolesEnum.ListOwner,
                                                                        AuthRolesEnum.Licensee,
                                                                    ]) && (!isViewMode || showEsignSaveSubmit)) && (
                                                                        <DaButton buttonStyle="primary" type="submit" sx={{ ml: 1 }}>Save and Submit</DaButton>
                                                                    )
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </ButtonBar>
                                            </Grid>
                                        </Grid>
                                    </Form>
                                </Grid>
                            ))}
                        </Grid>
                    </FormPaper>
                </>
            }
            <MessageBox
                isOpen={msgBoxOpen}
                onClose={msgBox_Close}
                {...msgBoxProps}
            />
            <DialogBox
                isOpen={dlgDelFilesOpen}
                onClose={dlgDelFiles_Close}
                {...dlgDelFilesProps}
            />
            <DialogBox
                isOpen={dlgConfirmOpen}
                onClose={dlgConfirm_Close}
                {...dlgConfirmProps}
            />
            <Dialog
                open={dlgRejectOpen}
                onClose={() => dlgReject_Close()}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Reject PAF</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <TextField
                            autoFocus
                            label="Rejection Reason"
                            multiline
                            rows={4}
                            fullWidth
                            required
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            placeholder="Please provide a reason for rejecting this PAF..."
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <DaButton buttonStyle="secondary" onClick={() => dlgReject_Close()}>
                        Cancel
                    </DaButton>
                    <DaButton
                        buttonStyle="primary"
                        onClick={() => dlgReject_Close('ok')}
                        disabled={!rejectionReason.trim()}
                    >
                        Reject
                    </DaButton>
                </DialogActions>
            </Dialog>
            <FileUploadDialog
                isOpen={dlgFilesUploadOpen}
                onClose={dlgFileUpload_Close}
                {...dlgFilesUploadProps}
            />
        </>
    );
}